import React, { useState } from "react";
import MagneticButton from "../components/MagneticButton";
import { submitContact } from "../lib/api";
import { ArrowUpRight, CheckCircle2, AlertCircle } from "lucide-react";

const Field = ({ label, name, value, onChange, type = "text", required, textarea, error, testId }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">{label}{required && <span className="text-[#6e5bff]"> *</span>}</span>
    {textarea ? (
      <textarea
        rows={5}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="mt-3 w-full bg-transparent border-b border-[#0a0a0b]/15 focus:border-[#6e5bff] outline-none py-3 text-base md:text-lg text-[#0a0a0b] placeholder-[#6e6e76] transition-colors resize-none"
        placeholder="Tell us what you're venturing into."
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="mt-3 w-full bg-transparent border-b border-[#0a0a0b]/15 focus:border-[#6e5bff] outline-none py-3 text-base md:text-lg text-[#0a0a0b] placeholder-[#6e6e76] transition-colors"
        placeholder=" "
      />
    )}
    {error && <span className="mt-2 text-xs text-[#ff6e6e] flex items-center gap-2"><AlertCircle size={12} />{error}</span>}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.message.trim()) e.message = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setState("submitting");
    setServerError("");
    try {
      await submitContact(form);
      setState("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setState("error");
      setServerError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 md:px-12 lg:px-20 py-32 md:py-40"
    >
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4 mb-10">
            <span className="h-px w-12 bg-[#0a0a0b]/30" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">06 — Contact</span>
          </div>
          <h2
            className="font-display font-medium tracking-tight leading-[1.02] text-[#0a0a0b]"
            style={{ fontSize: "clamp(2.4rem, 5.2vw, 5rem)" }}
            data-testid="contact-headline"
          >
            Let&apos;s delve into your <span className="accent-text">next venture.</span>
          </h2>
          <p className="mt-8 max-w-md text-base md:text-lg text-muted-ink">
            Tell us where you&apos;re stuck, where you want to go, and what&apos;s in the way. We&apos;ll come back with a sharp first move.
          </p>

          <div className="mt-12 space-y-5">
            <div>
              <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Email</span>
              <p className="mt-1 text-[#0a0a0b]">hello@delventure.com</p>
            </div>
            <div>
              <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Studio</span>
              <p className="mt-1 text-[#0a0a0b]">Remote · Worldwide</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-surface hairline rounded-[2px] p-8 md:p-12 space-y-8"
          noValidate
        >
          <div className="grid md:grid-cols-2 gap-8">
            <Field testId="contact-name" label="Name" name="name" value={form.name} onChange={onChange} required error={errors.name} />
            <Field testId="contact-email" label="Email" name="email" type="email" value={form.email} onChange={onChange} required error={errors.email} />
          </div>
          <Field testId="contact-company" label="Company" name="company" value={form.company} onChange={onChange} />
          <Field testId="contact-message" label="Message" name="message" value={form.message} onChange={onChange} textarea required error={errors.message} />

          <div className="flex items-center gap-6">
            <MagneticButton
              testId="contact-submit"
              onClick={onSubmit}
              className={state === "submitting" ? "opacity-60 pointer-events-none" : ""}
            >
              {state === "submitting" ? "Sending…" : "Send transmission"}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </MagneticButton>
          </div>

          {(state === "success" || state === "error") && (
            <div
              role="status"
              aria-live="polite"
              className="relative isolate mt-2 hairline rounded-[2px] px-4 py-3 bg-white/95"
              style={{ isolation: "isolate" }}
            >
              {state === "success" && (
                <div data-testid="contact-success" className="flex items-center gap-2 text-sm accent-text">
                  <CheckCircle2 size={16} /> Received. We&apos;ll be in touch within 24h.
                </div>
              )}
              {state === "error" && (
                <div data-testid="contact-error" className="flex items-center gap-2 text-sm text-[#ff6e6e]">
                  <AlertCircle size={16} /> {serverError}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
